import type { Maybe } from '@adornis/base/utilTypes.js';
import type { EntityData, PartialEntityData } from '@adornis/baseql/entities/types.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { ExpectedError } from '@adornis/validation/errors/ExpectedError.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { recursiveGroupResolver } from './recursiveGroupResolver.js';
import { assertNotInBilling } from './Group.js';
import { SchoolSubGroup } from './School.js';

/**
 * This is the standard group in most cases. Also reflected by translation. In 99% of cases a person creating a group wants it to be assigned to a kostenstelle.
 * If you ever need a group, in most cases it is this class.
 */
@Entity()
export class KostenstelleSubGroup extends SchoolSubGroup {
  public static override _class = 'KostenstelleSubGroup';

  @Field(() => Kostenstelle, {
    resolve: function (this: KostenstelleSubGroup) {
      return async (_gqlFields: BaseQLSelectionSet<KostenstelleSubGroup>) => {
        if (this.kostenstelle) return this.kostenstelle;
        return recursiveGroupResolver<Kostenstelle>(this, group => group._class === Kostenstelle._class);
      };
    },
  })
  kostenstelle: Maybe<Kostenstelle>;
}

@Entity()
export class Kostenstelle extends KostenstelleSubGroup {
  public static override _class = 'Kostenstelle';

  @Field(() => String)
  kostenstelleSuffix: Maybe<string>;

  static override async beforeCreate(entity: Kostenstelle): Promise<void> {
    entity.kostenstelleSuffix = padKostenstelleSuffix(entity.kostenstelleSuffix);
    await assertKostenstelleSuffixFree(entity.parentIDs ?? [], entity.kostenstelleSuffix, entity._id);
    return super.beforeCreate(entity);
  }

  /** The suffix is half of the Kostenstelle number the export books on. */
  static override async beforeUpdate(oldEntity: PartialEntityData<Kostenstelle>, update: Kostenstelle): Promise<void> {
    if (update.has('kostenstelleSuffix')) update.kostenstelleSuffix = padKostenstelleSuffix(update.kostenstelleSuffix);
    const suffixChanged =
      update.has('kostenstelleSuffix') &&
      padKostenstelleSuffix(oldEntity.kostenstelleSuffix) !== update.kostenstelleSuffix;
    assertNotInBilling(oldEntity, suffixChanged);

    // only a changed number is checked, so the duplicates from before this check stay editable
    const parentIDs = update.has('parentIDs') ? (update.parentIDs ?? []) : (oldEntity.parentIDs ?? []);
    const schoolChanged = [...parentIDs].sort().join() !== [...(oldEntity.parentIDs ?? [])].sort().join();
    if (suffixChanged || schoolChanged)
      await assertKostenstelleSuffixFree(
        parentIDs,
        update.has('kostenstelleSuffix') ? update.kostenstelleSuffix : oldEntity.kostenstelleSuffix,
        oldEntity._id,
      );

    return super.beforeUpdate(oldEntity, update);
  }
}

/** Kostenstelle numbers are the school prefix plus a two-digit suffix, the exports book on them as they are stored. */
export const padKostenstelleSuffix = (suffix: Maybe<string | number>) =>
  suffix == null || suffix === '' ? null : String(suffix).padStart(2, '0');

/** Another Kostenstelle of these schools on the same suffix - also one stored unpadded before suffixes were padded. */
export async function findKostenstelleWithSuffix(
  schoolIDs: string[],
  suffix: Maybe<string | number>,
  exceptID?: Maybe<string>,
) {
  const padded = padKostenstelleSuffix(suffix);
  if (!padded || !schoolIDs.length) return null;
  return (await getRawCollection<EntityData<Kostenstelle>>(Kostenstelle._collectionName)).findOne(
    {
      _class: Kostenstelle._class,
      parentIDs: { $in: schoolIDs },
      kostenstelleSuffix: { $in: [padded, String(Number(padded))] },
      ...(exceptID ? { _id: { $ne: exceptID } } : {}),
    },
    { projection: { name: 1 } },
  );
}

async function assertKostenstelleSuffixFree(schoolIDs: string[], suffix: Maybe<string>, exceptID: Maybe<string>) {
  const existing = await findKostenstelleWithSuffix(schoolIDs, suffix, exceptID);
  if (existing)
    throw new ExpectedError(
      `Der Kostenstellensuffix ${suffix} ist an dieser Schule bereits vergeben (${existing.name ?? 'ohne Namen'}).`,
    );
}
